import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// Attach bearer token (belt-and-suspenders alongside the httpOnly cookie).
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cc_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const RETRYABLE = new Set([429, 502, 503, 504]);

// Normalise every API error into a consistent, safe shape without swallowing it.
export function normalizeApiError(error) {
  const resp = error?.response;
  const status = resp?.status ?? 0;
  const requestId = resp?.headers?.["x-request-id"] || null;
  const data = resp?.data || {};
  return {
    status,
    code: data.reference || (data.detail && typeof data.detail === "object" ? data.detail.code : null) || null,
    requestId,
    reference: data.reference || (requestId ? String(requestId).slice(0, 8).toUpperCase() : null),
    message: formatApiErrorDetail(data.detail) || "Something went wrong. Please try again.",
    retryable: RETRYABLE.has(status) || status === 0,
  };
}

api.interceptors.response.use(
  (response) => {
    const rid = response?.headers?.["x-request-id"];
    if (rid) window.__cc_last_request_id = rid;
    return response;
  },
  (error) => {
    const norm = normalizeApiError(error);
    if (norm.requestId) window.__cc_last_request_id = norm.requestId;
    error.normalized = norm; // attach; do not swallow
    return Promise.reject(error);
  }
);

function syncNativeAuthToken(token) {
  // Capacitor's WKWebView exposes this bridge only in the native iOS shell.
  // Web remains unaffected. Sending auth changes immediately fixes both Siri
  // auth freshness and push-device registration/logout without lifecycle lag.
  try {
    window.webkit?.messageHandlers?.caterctrlAuth?.postMessage(token || "");
  } catch {
    // Native bridge is best-effort; browser auth remains authoritative.
  }
}

export function setToken(token) {
  if (token) localStorage.setItem("cc_token", token);
  else localStorage.removeItem("cc_token");
  syncNativeAuthToken(token);
}

export function getToken() {
  return localStorage.getItem("cc_token");
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
